import { computed, ref } from 'vue';
import SurveyFormsAPI from 'dashboard/api/surveyForms';
import {
  addQuestion as addQuestionTo,
  buildFormPayload,
  changeQuestionType,
  countValidationErrors,
  createEmptyForm,
  createOption,
  duplicateQuestion as duplicateQuestionAt,
  hasValidationErrors,
  isChoiceType,
  moveQuestion as moveQuestionTo,
  moveQuestionBy,
  normalizeForm,
  reindexQuestions,
  removeQuestion as removeQuestionAt,
  slugify,
  validateForm,
} from '../helper/surveyFormHelper';
import { apiErrorMessage, extractRecord } from './useSurveyForms';

/**
 * State machine of the survey builder.
 *
 * Owns the draft form, every structural mutation (add / remove / reorder /
 * retype a question), the derived validation and the save round-trip. Kept
 * free of i18n on purpose: validation returns codes and the Vue layer renders
 * the copy.
 */
export function useSurveyFormEditor({ kind = 'visit' } = {}) {
  const form = ref(createEmptyForm({ kind }));
  const baseline = ref(JSON.stringify(buildFormPayload(form.value)));
  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadError = ref('');
  const saveError = ref('');
  const showErrors = ref(false);

  const errors = computed(() => validateForm(form.value));
  const isValid = computed(() => !hasValidationErrors(errors.value));
  const errorCount = computed(() => countValidationErrors(errors.value));
  const isDirty = computed(
    () => JSON.stringify(buildFormPayload(form.value)) !== baseline.value
  );
  const isEditing = computed(() => Boolean(form.value.id));
  const questions = computed(() => form.value.questions);

  const questionErrors = key =>
    (showErrors.value && errors.value.question_errors[key]) || {};

  const commitBaseline = () => {
    baseline.value = JSON.stringify(buildFormPayload(form.value));
  };

  const setForm = raw => {
    form.value = normalizeForm(raw);
    showErrors.value = false;
    commitBaseline();
  };

  const resetForm = (nextKind = kind) => {
    form.value = createEmptyForm({ kind: nextKind });
    showErrors.value = false;
    commitBaseline();
  };

  const applyTemplate = templateDefinition => {
    const templateForm = templateDefinition?.form || templateDefinition;
    if (!templateForm || typeof templateForm !== 'object') return;

    form.value = normalizeForm(templateForm);
    showErrors.value = false;
    baseline.value = JSON.stringify(
      buildFormPayload(createEmptyForm({ kind: form.value.kind }))
    );
  };

  const setQuestions = list => {
    form.value.questions = reindexQuestions(list);
  };

  const updateForm = patch => {
    form.value = { ...form.value, ...patch };
  };

  const updateSuccessScreen = patch => {
    form.value.success_screen = { ...form.value.success_screen, ...patch };
  };

  const updateSettings = patch => {
    form.value.settings = { ...form.value.settings, ...patch };
  };

  const updateQuestion = (index, patch) => {
    const current = form.value.questions[index];
    if (!current) return;
    form.value.questions.splice(index, 1, { ...current, ...patch });
  };

  const addQuestion = (type = 'rating') => {
    setQuestions(addQuestionTo(form.value.questions, type));
    return form.value.questions[form.value.questions.length - 1];
  };

  const removeQuestion = index => {
    setQuestions(removeQuestionAt(form.value.questions, index));
  };

  const duplicateQuestion = index => {
    setQuestions(duplicateQuestionAt(form.value.questions, index));
  };

  const moveQuestion = (fromIndex, toIndex) => {
    setQuestions(moveQuestionTo(form.value.questions, fromIndex, toIndex));
  };

  const moveQuestionUp = index => {
    setQuestions(moveQuestionBy(form.value.questions, index, -1));
  };

  const moveQuestionDown = index => {
    setQuestions(moveQuestionBy(form.value.questions, index, 1));
  };

  const setQuestionType = (index, type) => {
    const current = form.value.questions[index];
    if (!current) return;
    form.value.questions.splice(index, 1, changeQuestionType(current, type));
  };

  const addOption = index => {
    const question = form.value.questions[index];
    if (!question || !isChoiceType(question.type)) return;
    question.options.push(createOption());
  };

  const removeOption = (index, optionIndex) => {
    const question = form.value.questions[index];
    if (!question || !isChoiceType(question.type)) return;
    question.options.splice(optionIndex, 1);
  };

  /**
   * Keeps the option `value` (the token stored in the answers hash) in sync
   * with the label while the operator is still typing a brand new option, and
   * freezes it once the option has been persisted with a value.
   */
  const updateOptionLabel = (index, optionIndex, label) => {
    const question = form.value.questions[index];
    const option = question?.options?.[optionIndex];
    if (!option) return;
    const previousSlug = slugify(option.label);
    option.label = label;
    if (!option.value || option.value === previousSlug) {
      option.value = slugify(label);
    }
  };

  const loadForm = async id => {
    isLoading.value = true;
    loadError.value = '';
    try {
      const { data } = await SurveyFormsAPI.show(id);
      setForm(extractRecord(data));
      return { ok: true };
    } catch (error) {
      loadError.value = apiErrorMessage(error);
      return { ok: false, message: loadError.value };
    } finally {
      isLoading.value = false;
    }
  };

  const save = async () => {
    showErrors.value = true;
    if (!isValid.value) return { ok: false, invalid: true };

    isSaving.value = true;
    saveError.value = '';
    const payload = buildFormPayload(form.value);

    try {
      const request = isEditing.value
        ? SurveyFormsAPI.update(form.value.id, payload)
        : SurveyFormsAPI.create(payload);
      const { data } = await request;
      const saved = extractRecord(data);
      setForm({ ...saved, id: saved.id ?? form.value.id });
      return { ok: true, data: form.value };
    } catch (error) {
      saveError.value = apiErrorMessage(error);
      return { ok: false, message: saveError.value };
    } finally {
      isSaving.value = false;
    }
  };

  return {
    form,
    questions,
    errors,
    errorCount,
    questionErrors,
    isValid,
    isDirty,
    isEditing,
    isLoading,
    isSaving,
    loadError,
    saveError,
    showErrors,
    setForm,
    resetForm,
    applyTemplate,
    commitBaseline,
    setQuestions,
    updateForm,
    updateSuccessScreen,
    updateSettings,
    updateQuestion,
    addQuestion,
    removeQuestion,
    duplicateQuestion,
    moveQuestion,
    moveQuestionUp,
    moveQuestionDown,
    setQuestionType,
    addOption,
    removeOption,
    updateOptionLabel,
    loadForm,
    save,
  };
}

export default useSurveyFormEditor;
